import { ActivityNote } from "shared/app/Notes/ActivityNote";
import { useAuthContext } from "providers/Auth";
import { useCallback, useMemo } from "react";
import { instance } from "api/apiBase";
import {
  getMentionNotificationId,
  isUnread,
  useMarkRead
} from "shared/app/Notes/helpers";
import { useQueryClient } from "@tanstack/react-query";
import { NoteForm } from "shared/app/Notes/components/NoteForm";
import { ApplicationStatus } from "shared/types";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Button, ButtonSize, Show } from "shared/components";
import {
  NoteEditIcon,
  NotificationBubbleIcon,
  ViewIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { NotesTabs } from "./components/NotesTabs";
import {
  ShowingNotesType,
  useNotesController
} from "shared/queries/useNotesController";
import { AutoLoadMoreOnScroll } from "shared/app/Notes/components/AutoLoadMoreOnScroll";
import { NotesAttachmentsTable } from "shared/app/Notes/components/NotesAttachmentsTable";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ApplicationNote,
  DeleteNoteMutation,
  EditNoteMutation
} from "shared/app/Notes/types";

const NotesTabContent = ({
  showingNotesType,
  showingNotes,
  notesController,
  propertyName,
  applicationId,
  deleteNoteMeta,
  editNoteMeta
}: {
  showingNotesType: ShowingNotesType;
  showingNotes: ApplicationNote[];
  notesController: ReturnType<typeof useNotesController>;
  propertyName?: string;
  applicationId: string;
  deleteNoteMeta: DeleteNoteMutation;
  editNoteMeta: EditNoteMutation;
}) => {
  const { fetchNextPage } = notesController.notesMeta;
  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  if (showingNotesType === ShowingNotesType.ATTACHMENTS)
    return <NotesAttachmentsTable notesController={notesController} />;

  if (!showingNotes.length)
    return (
      <div className="flex flex-auto flex-col items-center justify-center">
        <div className="mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-background-800 p-5">
          <HugeiconsIcon
            icon={
              showingNotesType === ShowingNotesType.ALL
                ? NoteEditIcon
                : NotificationBubbleIcon
            }
            size={32}
            strokeWidth={1.5}
            className="text-content-400"
          />
        </div>
        <p className="text-base font-bold text-content-500">
          {showingNotesType === ShowingNotesType.ALL
            ? "No notes yet"
            : "No mentions yet"}
        </p>
        <p className="text-sm font-medium text-content-300">
          {showingNotesType === ShowingNotesType.ALL
            ? "Be the first to add a note about this application"
            : "Type @ followed by a name to mention someone"}
        </p>
      </div>
    );

  return (
    <div className="size-full flex-grow overflow-x-hidden overflow-y-auto bg-background-900 py-6">
      <div className="border-alpha-100 w-full space-y-6 pb-6 px-6">
        {showingNotes.map((note) => (
          <ActivityNote
            key={showingNotesType + note.updated_at + note.created_at}
            propertyName={propertyName}
            applicationId={applicationId}
            note={note}
            deleteNoteMeta={deleteNoteMeta}
            editNoteMeta={editNoteMeta}
          />
        ))}
        <AutoLoadMoreOnScroll
          hasNextPage={Boolean(notesController.notesMeta.hasNextPage)}
          isFetchingNextPage={notesController.notesMeta.isFetchingNextPage}
          onLoadMore={handleLoadMore}
        />
      </div>
    </div>
  );
};

export const NotesList = ({
  applicationId,
  notesController,
  propertyName,
  applicationStatus,
  applicantsNames,
  showViewApplicationButton,
  onMarkReadSuccess
}: {
  applicationId: string;
  propertyName?: string;
  applicationStatus?: ApplicationStatus;
  applicantsNames?: string;
  notesController: ReturnType<typeof useNotesController>;
  showViewApplicationButton?: boolean;
  onMarkReadSuccess?: () => void;
}) => {
  const { auth } = useAuthContext();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    addNoteMeta,
    editNoteMeta,
    deleteNoteMeta,
    showingNotesType,
    showingNotes
  } = notesController;

  const ureadNotes = useMemo(() => {
    const newUnreadNotes: { id: string; read: boolean }[] = [];
    const userId = auth?.id;
    if (userId == null) return newUnreadNotes;

    for (const note of showingNotes)
      if (isUnread(note, userId))
        newUnreadNotes.push({
          id: String(getMentionNotificationId(note, userId) ?? ""),
          read: false
        });

    return newUnreadNotes;
  }, [showingNotes, auth?.id]);

  const getElById = (id: string) =>
    document.querySelector<HTMLElement>(`[data-activity-id="${id}"]`);

  async function markReadBatch(ids: string[]) {
    await instance
      .post("/portal/v1/notifications/mark_as_read", {
        ids: ids.map(Number)
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
        });
        onMarkReadSuccess?.();
      });
  }

  useMarkRead(ureadNotes, getElById, markReadBatch, {
    threshold: 0.5,
    dwellMs: 600,
    flushMs: 300
  });

  const handleViewApplication = () => {
    navigate(`/applications/${applicationId}`);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 py-4 border-b space-y-4">
        <div className="space-y-2">
          <ApplicationStatusBadge
            type="application"
            status={applicationStatus}
            className="text-xs font-semibold"
          />
          <p className="text-sm font-bold text-content-1000">
            {applicantsNames}
          </p>
        </div>
        <Show if={showViewApplicationButton}>
          <Button
            HugeIcon={ViewIcon}
            size={ButtonSize.XS}
            onClick={handleViewApplication}
          >
            View application
          </Button>
        </Show>
      </div>
      <div className="p-6 border-b">
        <NoteForm
          onSubmit={async (data) => {
            await addNoteMeta.mutateAsync(data);
          }}
          applicationId={String(applicationId)}
          propertyName={propertyName}
          isLoading={addNoteMeta.isPending}
          editorContainerClassName="min-h-[100px]"
        />
      </div>

      <div className="flex flex-col flex-grow bg-background-900 overflow-hidden">
        <div className="px-6">
          <NotesTabs notesController={notesController} tabClassName="mt-4" />
        </div>
        <NotesTabContent
          showingNotesType={showingNotesType}
          showingNotes={showingNotes}
          notesController={notesController}
          propertyName={propertyName}
          applicationId={String(applicationId)}
          deleteNoteMeta={deleteNoteMeta}
          editNoteMeta={editNoteMeta}
        />
      </div>
    </div>
  );
};
